import axios from 'axios';
import { PageSpeedResult } from '../types';
import { getApiUrl } from '../config/environment';
import { io, Socket } from 'socket.io-client';
import { logger } from '../utils/logger';

// 환경별 설정은 중앙 관리됨 (src/config/environment.ts)
// 임시로 이전 방식 유지하되, 향후 getApiUrl() 사용으로 전환 예정
export const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3300';

// 백엔드 연결 상태 확인 함수
export const checkBackendConnection = async (): Promise<boolean> => {
  try {
    console.log('🔍 백엔드 연결 상태 확인:', BACKEND_URL);
    const response = await axios.get(`${BACKEND_URL}/api/health`, {
      timeout: 5000 // 5초 타임아웃
    });
    
    if (response.status === 200) {
      console.log('✅ 백엔드 연결 정상:', response.data);
      return true;
    } else {
      console.error('❌ 백엔드 응답 오류:', response.status);
      return false;
    }
  } catch (error) {
    console.error('❌ 백엔드 연결 실패:', error);
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNREFUSED') {
        console.error('백엔드 서버가 실행되지 않았거나 연결을 거부했습니다.');
      } else if (error.code === 'ECONNABORTED') {
        console.error('백엔드 연결 시간이 초과되었습니다.');
      } else if (error.request) {
        console.error('네트워크 연결에 문제가 있습니다.');
      }
    }
    return false;
  }
};

// LLM을 이용한 HTML 분석 함수 (OpenAI GPT-4o 사용)
export const analyzeHtml = async (url: string, retryCount: number = 0): Promise<string | null> => {
  const maxRetries = 2;
  const timeoutDuration = 600000; // 10분 타임아웃으로 증가

  try {
    console.log(`🤖 OpenAI 분석 요청 시작 (프론트엔드) - 시도 ${retryCount + 1}/${maxRetries + 1}:`, url);
    console.log(`⏱️ 타임아웃 설정: ${timeoutDuration / 1000}초`);

    const response = await axios.post(`${BACKEND_URL}/api/analyze-html`, { url }, {
      timeout: timeoutDuration,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    console.log('📡 백엔드 응답 상태:', response.status);
    console.log('📡 백엔드 응답 데이터:', response.data);

    if (response.data.success && response.data.analysis) {
      // analysis가 객체인 경우 llmAnalysis 필드를 추출
      const analysis = response.data.analysis;
      const llmAnalysisText = typeof analysis === 'string'
        ? analysis
        : (analysis.llmAnalysis || analysis);

      console.log('✅ OpenAI GPT-4o 분석 성공! 결과 타입:', typeof analysis);
      console.log('✅ LLM 분석 텍스트 길이:',
        typeof llmAnalysisText === 'string' ? llmAnalysisText.length : 'not a string');

      return typeof llmAnalysisText === 'string' ? llmAnalysisText : null;
    } else {
      console.error('❌ OpenAI 분석 API 응답 오류:', response.data.error);
      throw new Error(response.data.error || 'OpenAI 분석 실패');
    }
  } catch (error) {
    console.error(`❌ OpenAI 분석 요청 실패 (시도 ${retryCount + 1}):`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED' && retryCount < maxRetries) {
        console.warn(`⚠️ 타임아웃 발생 - ${retryCount + 1}회 재시도 중...`);
        await new Promise(resolve => setTimeout(resolve, 2000)); // 2초 대기
        return analyzeHtml(url, retryCount + 1);
      }

      if (error.response) {
        console.error('서버 응답 오류:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('네트워크 오류: 백엔드 서버에 연결할 수 없습니다');
      } else {
        console.error('요청 설정 오류:', error.message);
      }
    }

    // 재시도 후에도 실패하면 null 반환
    return null;
  }
};

// 완전한 분석 결과를 받아오는 함수 (timestamp, resourceSummary 등 포함)
export const analyzeComplete = async (url: string, retryCount: number = 0): Promise<any | null> => {
  const maxRetries = 2;
  const timeoutDuration = 600000;

  try {
    console.log(`🔍 완전한 분석 요청 시작 - 시도 ${retryCount + 1}/${maxRetries + 1}:`, url);

    const response = await axios.post(`${BACKEND_URL}/api/analyze-html`, { url }, {
      timeout: timeoutDuration,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    console.log('📡 완전한 분석 응답:', response.data);

    if (response.data.success && response.data.analysis) {
      // 전체 분석 객체 반환 (timestamp, resourceSummary, importantInfo 포함)
      console.log('✅ 완전한 분석 성공! timestamp:', response.data.analysis.timestamp);
      return response.data.analysis;
    } else {
      console.error('❌ 분석 API 응답 오류:', response.data.error);
      throw new Error(response.data.error || '분석 실패');
    }
  } catch (error) {
    console.error(`❌ 완전한 분석 요청 실패 (시도 ${retryCount + 1}):`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED' && retryCount < maxRetries) {
        console.warn(`⚠️ 타임아웃 발생 - ${retryCount + 1}회 재시도 중...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return analyzeComplete(url, retryCount + 1);
      }
    }

    return null;
  }
};

// PageSpeed Insights API 호출 함수 (백엔드 경유)
export const analyzePageSpeed = async (url: string, retryCount: number = 0): Promise<PageSpeedResult | null> => {
  const maxRetries = 2;
  const timeoutDuration = 600000; // 10분 타임아웃으로 증가
  
  try {
    console.log(`📊 PageSpeed 분석 시작 (백엔드 경유) - 시도 ${retryCount + 1}/${maxRetries + 1}:`, url);
    console.log(`⏱️ 타임아웃 설정: ${timeoutDuration / 1000}초`);

    const response = await axios.post(`${BACKEND_URL}/api/pagespeed`, {
      url: url
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: timeoutDuration,
    });

    if (response.data.success && response.data.data) {
      console.log('✅ PageSpeed 분석 완료:', response.data.data);
      return response.data.data;
    } else {
      console.error('❌ PageSpeed API 응답 오류:', response.data.error);
      return null;
    }

  } catch (error) {
    console.error(`❌ PageSpeed 분석 요청 실패 (시도 ${retryCount + 1}):`, error);
    
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED' && retryCount < maxRetries) {
        console.warn(`⚠️ PageSpeed API 타임아웃 발생 - ${retryCount + 1}회 재시도 중...`);
        await new Promise(resolve => setTimeout(resolve, 3000)); // 3초 대기 (PageSpeed는 더 오래 걸릴 수 있음)
        return analyzePageSpeed(url, retryCount + 1);
      } else if (error.code === 'ECONNABORTED') {
        console.error('PageSpeed API 요청 시간 초과 (최대 재시도 초과)');
      } else if (error.response) {
        console.error(`PageSpeed API 서버 오류: ${error.response.status} - ${error.response.data?.error || error.response.statusText}`);
      } else if (error.request) {
        console.error('PageSpeed API 서버 연결 실패');
      }
    }
    
    return null;
  }
};

// WebSocket 연결 관리
let socket: Socket | null = null;

// WebSocket 연결 함수
export const connectWebSocket = (sessionId?: string): Promise<Socket> => {
  return new Promise((resolve, reject) => {
    // sessionId가 변경되었으면 기존 연결 끊고 재연결
    if (socket && socket.connected && sessionId) {
      console.log('🔄 sessionId 변경 감지 - WebSocket 재연결:', sessionId);
      socket.disconnect();
      socket = null;
    } else if (socket && socket.connected) {
      console.log('✅ 기존 WebSocket 연결 재사용 (sessionId 없음)');
      resolve(socket);
      return;
    }

    console.log('🔌 WebSocket 연결 시작:', BACKEND_URL, sessionId ? `(세션: ${sessionId})` : '');

    socket = io(BACKEND_URL, {
      query: sessionId ? { sessionId } : {},  // sessionId를 query parameter로 전달
      timeout: 10000, // 10초 타임아웃
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
    });

    socket.on('connect', () => {
      console.log('✅ WebSocket 연결 성공:', socket?.id);
      resolve(socket!);
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket 연결 해제:', reason);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ WebSocket 연결 실패:', error);
      reject(error);
    });

    // 10초 후에도 연결되지 않으면 타임아웃
    setTimeout(() => {
      if (!socket?.connected) {
        reject(new Error('WebSocket 연결 타임아웃'));
      }
    }, 10000);
  });
};

// WebSocket 연결 정리 함수
export const disconnectWebSocket = () => {
  if (socket) {
    console.log('🔌 WebSocket 연결 정리');
    socket.disconnect();
    socket = null;
  }
};

// Hall of Fame 데이터 가져오기
export const fetchHallOfFameData = async () => {
  try {
    console.log('📊 Hall of Fame 데이터 요청 중...');
    const response = await axios.get(`${BACKEND_URL}/api/hall-of-fame`, {
      timeout: 10000 // 10초 타임아웃
    });

    // API 응답 구조: {success: true, data: [...]}
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      console.log(`✅ Hall of Fame 데이터 로드 완료: ${response.data.data.length}개 사이트`);
      return response.data.data;
    } else if (response.data && Array.isArray(response.data)) {
      // 폴백: 직접 배열인 경우
      console.log(`✅ Hall of Fame 데이터 로드 완료: ${response.data.length}개 사이트`);
      return response.data;
    } else {
      console.warn('⚠️ Hall of Fame 데이터 형식 오류:', response.data);
      return [];
    }
  } catch (error) {
    console.error('❌ Hall of Fame 데이터 로드 실패:', error);
    return [];
  }
};

// ============================================================================
// CrUX API (Chrome UX Report - 실제 사용자 경험 데이터)
// ============================================================================

// CrUX 메트릭 분포 타입
export interface CruxDistribution {
  good: number;
  needsImprovement: number;
  poor: number;
  p75?: number;
}

// CrUX 데이터 타입
export interface CruxData {
  largestContentfulPaint: number | null;
  firstContentfulPaint: number | null;
  cumulativeLayoutShift: number | null;
  interactionToNextPaint: number | null;
  timeToFirstByte: number | null;
  lcpDistribution?: CruxDistribution;
  fcpDistribution?: CruxDistribution;
  clsDistribution?: CruxDistribution;
  inpDistribution?: CruxDistribution;
  ttfbDistribution?: CruxDistribution;
}

// CrUX 응답 타입
export interface CruxResponse {
  hasData: boolean;
  mobile: {
    data: CruxData | null;
    source: 'url' | 'origin' | 'stub' | null;
  };
  desktop: {
    data: CruxData | null;
    source: 'url' | 'origin' | 'stub' | null;
  };
  timestamp: string;
  url: string;
  error?: string;
  isStub?: boolean;
}

// CrUX 비교 결과 타입
export interface CruxComparison {
  available: boolean;
  message?: string;
  mobile?: {
    source: string;
    lcp: { lighthouse: number; crux: number; difference: string };
    fcp: { lighthouse: number; crux: number; difference: string };
    cls: { lighthouse: number; crux: number; difference: string };
    interactivity: { lighthouseTbt: number; cruxInp: number; note: string };
    ttfb: { crux: number };
  };
  desktop?: {
    source: string;
    lcp: { lighthouse: number; crux: number; difference: string };
    fcp: { lighthouse: number; crux: number; difference: string };
    cls: { lighthouse: number; crux: number; difference: string };
    interactivity: { lighthouseTbt: number; cruxInp: number; note: string };
    ttfb: { crux: number };
  };
}

/**
 * CrUX 데이터 조회
 * Chrome UX Report - 실제 Chrome 사용자들의 필드 데이터
 *
 * @param url - 분석할 URL
 * @returns CrUX 데이터 (모바일 + 데스크탑)
 */
export const fetchCruxData = async (url: string): Promise<CruxResponse | null> => {
  try {
    console.log('📊 CrUX 실제 사용자 데이터 요청 중:', url);

    const response = await axios.post(`${BACKEND_URL}/api/crux`, { url }, {
      timeout: 30000, // 30초 타임아웃
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.data && response.data.success) {
      const data = response.data.data as CruxResponse;
      console.log(`✅ CrUX 데이터 로드 완료 - 데이터 존재: ${data.hasData}`);

      if (data.hasData) {
        console.log(`  - 모바일 소스: ${data.mobile.source || '없음'}`);
        console.log(`  - 데스크탑 소스: ${data.desktop.source || '없음'}`);
      }

      return data;
    } else {
      console.warn('⚠️ CrUX 데이터 응답 오류:', response.data.error);
      return null;
    }
  } catch (error) {
    console.error('❌ CrUX 데이터 로드 실패:', error);
    return null;
  }
};

/**
 * CrUX vs Lighthouse 비교 데이터 조회
 * Lab 데이터(Lighthouse)와 Field 데이터(CrUX) 차이 분석
 *
 * @param url - 분석할 URL
 * @param lighthouseData - Lighthouse 분석 결과 (선택사항)
 * @returns 비교 분석 결과
 */
export const fetchCruxComparison = async (
  url: string,
  lighthouseData?: { mobile?: any; desktop?: any }
): Promise<{
  crux: CruxResponse;
  comparison: CruxComparison | null;
  summary: {
    hasCruxData: boolean;
    mobileSource: string | null;
    desktopSource: string | null;
    message: string;
  };
} | null> => {
  try {
    console.log('📊 CrUX vs Lighthouse 비교 분석 요청 중:', url);

    const response = await axios.post(`${BACKEND_URL}/api/crux-compare`, {
      url,
      lighthouseData
    }, {
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.data && response.data.success) {
      console.log('✅ CrUX 비교 분석 완료');
      return response.data.data;
    } else {
      console.warn('⚠️ CrUX 비교 분석 응답 오류:', response.data.error);
      return null;
    }
  } catch (error) {
    console.error('❌ CrUX 비교 분석 실패:', error);
    return null;
  }
};